import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LinearProgress } from '../sectepe-ui/index';
import { PageHeader, StatusBadge, StatusTone } from '../components/ui';
import { Icon } from '../components/icons';
import {
  FLOW_PHASES,
  FLOW_STEP_COUNT,
  SIDE_STEPS,
  LinkedInConnectionCard,
  useLinkedInFlow,
  useLinkedInCallbackParams,
  phaseState,
  phasePath,
  StepStatus,
} from '../linkedin';

const STATE_LABEL: Record<StepStatus['state'], { label: string; tone: StatusTone }> = {
  done: { label: 'Erledigt', tone: 'success' },
  ready: { label: 'Bereit', tone: 'primary' },
  blocked: { label: 'Voraussetzung fehlt', tone: 'neutral' },
};

/**
 * LinkedIn-Studio: die Uebersicht mit echtem Fortschritt. Die neun Schritte
 * stehen als fuenf Phasenkarten da; jede zeigt, ob ihre Schritte erledigt,
 * bereit oder gesperrt sind, und der naechste sinnvolle Schritt steht ganz
 * oben.
 */
const GesamtprozessPage: React.FC = () => {
  const navigate = useNavigate();
  const { status, loading, loaded, error, refresh, data } = useLinkedInFlow();
  useLinkedInCallbackParams(refresh);

  const next = status.nextStep;
  const allDone = loaded && next === null;

  return (
    <div className="page-container-wide">
      <PageHeader
        eyebrow={<><Icon name="linkedin" size={14} /> LinkedIn-Studio</>}
        title="Übersicht & Fortschritt"
        subtitle="Von der Strategie bis zur Veröffentlichung – in der richtigen Reihenfolge, mit Blick auf das, was schon da ist."
        actions={(
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => refresh()} disabled={loading}>
            <Icon name="refresh" size={14} /> {loading ? 'Aktualisiere…' : 'Aktualisieren'}
          </button>
        )}
      />

      {error && (
        <div className="alert alert-warning">
          Teile des Prozessstatus konnten nicht geladen werden ({error}). Die Anzeige kann unvollständig sein.
        </div>
      )}

      {/* Hero: Fortschritt + naechster Schritt */}
      <section className="flow-hero card" aria-label="Nächster Schritt">
        <div className="flow-hero__progress">
          <div className="flow-hero__progress-head">
            <span className="flow-hero__label">Fortschritt</span>
            <strong>{status.doneCount} von {FLOW_STEP_COUNT} Schritten erledigt</strong>
          </div>
          <LinearProgress value={status.progressPercent} aria-label="Fortschritt im LinkedIn-Prozess" />
        </div>
        <div className="flow-hero__next">
          {allDone ? (
            <>
              <span className="flow-hero__label">Alle Schritte erledigt</span>
              <h2>Nächste Runde: neuer Beitrag</h2>
              <p className="text-secondary">
                Nutze die Statistiken, um zu sehen, was funktioniert hat, und erstelle den nächsten Beitrag.
              </p>
              <div className="btn-group">
                <button type="button" className="btn btn-primary" onClick={() => navigate('/linkedin-post-generator')}>
                  Neuen Beitrag erstellen <Icon name="arrow-right" size={14} />
                </button>
                <Link to="/linkedin-statistics" className="btn btn-secondary">Statistiken</Link>
              </div>
            </>
          ) : next ? (
            <>
              <span className="flow-hero__label">Nächster Schritt</span>
              <h2>{next.index} · {next.label}</h2>
              <p className="text-secondary">{next.description}</p>
              <div className="btn-group">
                <button type="button" className="btn btn-primary" onClick={() => navigate(next.path)}>
                  {next.cta} <Icon name="arrow-right" size={14} />
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="flow-hero__label">Nächster Schritt</span>
              <h2>Wird ermittelt…</h2>
              <div className="btn-group">
                <button type="button" className="btn btn-primary" onClick={() => navigate('/strategy')}>
                  Mit der Strategie beginnen <Icon name="arrow-right" size={14} />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {status.warnings.length > 0 && (
        <div className="alert alert-warning flow-warnings" role="status">
          <Icon name="alert" size={16} />
          <ul>
            {status.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Fuenf Phasen mit ihren Schritten */}
      <h2 className="flow-section-title">Der Prozess in fünf Phasen</h2>
      <ol className="flow-grid flow-grid--phases" aria-label="Prozessschritte">
        {FLOW_PHASES.map((phase) => {
          const ps = phaseState(status, phase);
          const meta = STATE_LABEL[ps.state];
          const isNext = Boolean(next && phase.steps.includes(next.id));
          const target = ps.nextStep ? ps.nextStep.step.path : phasePath(phase);
          return (
            <li key={phase.id} className={`flow-card${isNext ? ' flow-card--next' : ''}`} data-state={ps.state}>
              <div className="flow-card__head">
                <span className="flow-card__index" aria-hidden="true">
                  {ps.state === 'done' ? <Icon name="check" size={14} /> : phase.index}
                </span>
                <span className="flow-card__icon" aria-hidden="true"><Icon name={phase.icon} size={18} /></span>
                <StatusBadge tone={meta.tone} className="flow-card__badge">{isNext ? 'Als Nächstes' : meta.label}</StatusBadge>
              </div>
              <h3 className="flow-card__title">
                <Link to={phasePath(phase)}>{phase.index} · {phase.label}</Link>
              </h3>
              <p className="flow-card__text">{phase.description}</p>
              <ul className="flow-card__steps" aria-label={`Schritte: ${phase.label}`}>
                {ps.steps.map((s) => (
                  <li key={s.step.id} data-state={s.state}>
                    <span className="flow-card__step-state" aria-hidden="true">
                      {s.state === 'done' ? <Icon name="check" size={11} /> : s.state === 'blocked' ? <Icon name="lock" size={10} /> : null}
                    </span>
                    <Link to={s.step.path}>{s.step.label}</Link>
                    {s.state === 'done' && <span className="flow-card__step-meta">{s.step.doneLabel(s.count)}</span>}
                    {s.state === 'blocked' && s.missing.length > 0 && (
                      <span className="flow-card__step-meta">
                        braucht zuerst{' '}
                        {s.missing.map((m, i) => (
                          <React.Fragment key={m.id}>
                            {i > 0 && ', '}
                            <Link to={m.path}>{m.label}</Link>
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                    {s.state === 'ready' && s.count === 0 && <span className="flow-card__step-meta">noch nichts angelegt</span>}
                  </li>
                ))}
                {phase.extraTabs.map((t) => (
                  <li key={t.path} data-state="extra">
                    <span className="flow-card__step-state" aria-hidden="true"><Icon name={t.icon} size={10} /></span>
                    <Link to={t.path}>{t.label}</Link>
                  </li>
                ))}
              </ul>
              {ps.nextStep && ps.nextStep.hints.length > 0 && ps.nextStep.state !== 'blocked' && (
                <ul className="flow-card__hints">
                  {ps.nextStep.hints.map((h) => <li key={h}>{h}</li>)}
                </ul>
              )}
              <div className="flow-card__actions">
                <Link to={target} className={`btn btn-sm ${isNext ? 'btn-primary' : 'btn-secondary'}`}>
                  {ps.nextStep ? ps.nextStep.step.cta : 'Öffnen'}
                </Link>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Ergaenzende Werkzeuge */}
      <h2 className="flow-section-title">Ergänzend</h2>
      <ul className="flow-side-grid" aria-label="Ergänzende Werkzeuge">
        {SIDE_STEPS.map((s) => (
          <li key={s.id} className="flow-side-card">
            <span className="flow-card__icon" aria-hidden="true"><Icon name={s.icon} size={18} /></span>
            <div>
              <Link to={s.path} className="flow-side-card__title">{s.label}</Link>
              <p className="flow-card__text">{s.description}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="flow-two-col">
        <LinkedInConnectionCard variant="compact" onChanged={refresh} />

        <section className="card flow-publish-paths" aria-label="Wege zur Veröffentlichung">
          <h2 className="card-title">Drei Wege zur Veröffentlichung</h2>
          <p className="card-description">
            Intermal hat historisch drei Pfade. Damit du weißt, welcher was tut:
          </p>
          <dl>
            <dt><Link to="/publication-calendar">Veröffentlichungskalender</Link></dt>
            <dd>
              Redaktioneller Plan für Beiträge aus dem Generator. „Veröffentlicht“ ist hier eine Markierung – gepostet
              wird manuell oder über „Freigabe & Versand“.
              {data.calendar.total > 0 && <> Aktuell {data.calendar.total} Einträge, {data.calendar.published} veröffentlicht.</>}
            </dd>
            <dt><Link to="/scheduled-posts">Freigabe &amp; Versand</Link></dt>
            <dd>
              Freigabe-Warteschlange mit echtem Posten über das verbundene LinkedIn-Konto. Wird aus „Text generieren“ befüllt.
              {data.scheduledPosts.pending > 0 && <> {data.scheduledPosts.pending} warten auf Freigabe.</>}
            </dd>
            <dt><Link to="/flyer-generator">Flyer</Link></dt>
            <dd>Bild-Flyer aus Beitragsideen (unter „Inhalte &amp; Werkzeuge → Medien“); posten direkt auf LinkedIn, ohne Kalender.</dd>
          </dl>
        </section>
      </div>
    </div>
  );
};

export default GesamtprozessPage;
